import { ReloadOutlined, SearchOutlined, SlidersOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { CurrentUser } from "../../api/currentUser";
import { ApiProblem } from "../../api/httpClient";
import { useApiResource } from "../../api/useApiResource";
import { useActorContext } from "../../auth/ActorContext";
import { can } from "../../auth/effectivePermissions";
import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { ForbiddenState } from "../../components/ForbiddenState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { PageHeading } from "../../components/PageHeading";
import { StaleState } from "../../components/StaleState";
import { StatusBadge } from "../../components/StatusBadge";
import {
  exportMartyrs,
  searchMartyrs,
  type MartyrExportFilters,
  type MartyrListRow,
  type MartyrSearchFilters,
} from "./martyrApi";
import {
  identityStatusLabel,
  identityStatusTone,
} from "./martyrVocabulary";
import { useCatalog } from "../catalogs/useCatalog";
import { DATE_PICKER_MASK_FORMAT, formatDateVn } from "../../lib/dateFormat";
import { searchCemeteries } from "../cemeteries/cemeteryApi";
import "./martyrs.css";

const PAGE_SIZE = 20;

/**
 * Draft/applied của thanh lọc SCR-025. Trạng thái ĐANG ÁP DỤNG nằm trên URL query
 * (spec §6 yêu cầu URL query state cho ba danh sách S11.6, khác `NccListPage` dùng
 * state cục bộ); draft chỉ là bản nháp người dùng đang gõ.
 */
type MartyrDraft = Readonly<{
  q: string;
  identityStatus: string;
  cemeteryId: string;
  sacrificeFromDate: string;
  sacrificeToDate: string;
  nativePlace: string;
}>;

const EMPTY_DRAFT: MartyrDraft = {
  q: "",
  identityStatus: "",
  cemeteryId: "",
  sacrificeFromDate: "",
  sacrificeToDate: "",
  nativePlace: "",
};

const DRAFT_FIELDS: ReadonlyArray<keyof MartyrDraft> = [
  "q",
  "identityStatus",
  "cemeteryId",
  "sacrificeFromDate",
  "sacrificeToDate",
  "nativePlace",
];

/** Điều kiện chỉ thuộc panel nâng cao — dùng cho badge trên nút "Lọc". */
const ADVANCED_FIELDS: ReadonlyArray<keyof MartyrDraft> = [
  "sacrificeFromDate",
  "sacrificeToDate",
  "nativePlace",
];

function readDraft(params: URLSearchParams): MartyrDraft {
  const read = (key: keyof MartyrDraft): string => params.get(key) ?? "";
  return {
    q: read("q"),
    identityStatus: read("identityStatus"),
    cemeteryId: read("cemeteryId"),
    sacrificeFromDate: read("sacrificeFromDate"),
    sacrificeToDate: read("sacrificeToDate"),
    nativePlace: read("nativePlace"),
  };
}

/** `page` trên URL là số trang người dùng đọc được (1-based); API nhận 0-based. */
function readPage(params: URLSearchParams): number {
  const raw = Number(params.get("page") ?? "1");
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
}

function toSearchParams(draft: MartyrDraft, page: number): URLSearchParams {
  const params = new URLSearchParams();
  for (const field of DRAFT_FIELDS) {
    const value = draft[field];
    if (value !== "") params.set(field, value);
  }
  if (page > 1) params.set("page", String(page));
  return params;
}

/** Predicate dùng chung cho list và export (export không phân trang — rowLimit do server). */
function baseFilters(draft: MartyrDraft): MartyrExportFilters {
  const clean = (value: string): string | undefined => (value === "" ? undefined : value);
  const q = clean(draft.q);
  const identityStatus = clean(draft.identityStatus);
  const cemeteryId = clean(draft.cemeteryId);
  const sacrificeFromDate = clean(draft.sacrificeFromDate);
  const sacrificeToDate = clean(draft.sacrificeToDate);
  const nativePlace = clean(draft.nativePlace);
  return {
    ...(q !== undefined ? { q } : {}),
    ...(identityStatus !== undefined ? { identityStatus } : {}),
    ...(cemeteryId !== undefined ? { cemeteryId } : {}),
    ...(sacrificeFromDate !== undefined ? { sacrificeFromDate } : {}),
    ...(sacrificeToDate !== undefined ? { sacrificeToDate } : {}),
    ...(nativePlace !== undefined ? { nativePlace } : {}),
  };
}

function toApiFilters(draft: MartyrDraft, page: number): MartyrSearchFilters {
  return { ...baseFilters(draft), page: page - 1, size: PAGE_SIZE };
}

type MartyrChip = Readonly<{ fields: ReadonlyArray<keyof MartyrDraft>; label: string }>;

/** Chip "Đã lọc" dựng từ điều kiện ĐANG ÁP DỤNG (URL), không phải draft đang gõ. */
function buildChips(
  draft: MartyrDraft,
  cemeteryOptions: ReadonlyArray<{ value: string; label: string }> = [],
  identityStatusOptions?: readonly { value: string; label: string }[],
): MartyrChip[] {
  const chips: MartyrChip[] = [];
  if (draft.q !== "") chips.push({ fields: ["q"], label: `từ khóa: "${draft.q}"` });
  if (draft.identityStatus !== "") {
    const opt = identityStatusOptions?.find((o) => o.value === draft.identityStatus);
    chips.push({
      fields: ["identityStatus"],
      label: `danh tính: ${opt?.label ?? identityStatusLabel(draft.identityStatus)}`,
    });
  }
  if (draft.cemeteryId !== "") {
    const matched = cemeteryOptions.find((opt) => opt.value === draft.cemeteryId);
    const label = matched ? matched.label : draft.cemeteryId;
    chips.push({ fields: ["cemeteryId"], label: `nghĩa trang: ${label}` });
  }
  if (draft.sacrificeFromDate !== "" || draft.sacrificeToDate !== "") {
    const from = formatDateVn(draft.sacrificeFromDate);
    const to = formatDateVn(draft.sacrificeToDate);
    const label = draft.sacrificeFromDate !== "" && draft.sacrificeToDate !== ""
      ? `hy sinh: ${from} – ${to}`
      : draft.sacrificeFromDate !== ""
        ? `hy sinh từ: ${from}`
        : `hy sinh đến: ${to}`;
    chips.push({ fields: ["sacrificeFromDate", "sacrificeToDate"], label });
  }
  if (draft.nativePlace !== "") {
    chips.push({ fields: ["nativePlace"], label: `quê quán: ${draft.nativePlace}` });
  }
  return chips;
}

function advancedCount(draft: MartyrDraft): number {
  return ADVANCED_FIELDS.filter((field) => draft[field] !== "").length;
}

/** "An táng": nghĩa trang + mã mộ; cả hai thiếu thì nói thật là chưa có thông tin mộ. */
function BurialCell({ row }: { readonly row: MartyrListRow }) {
  const cemeteryName = row.cemeteryName?.trim() ?? "";
  const graveCode = row.graveCode?.trim() ?? "";
  if (cemeteryName === "" && graveCode === "") {
    return <Typography.Text type="secondary">Chưa có thông tin mộ</Typography.Text>;
  }
  return (
    <span className="s11-cell-stack">
      <span>{cemeteryName !== "" ? cemeteryName : "Chưa có thông tin nghĩa trang"}</span>
      {graveCode !== "" ? <span className="s11-cell-sub">Mã mộ: {graveCode}</span> : null}
    </span>
  );
}

/**
 * SCR-025 — Danh sách liệt sĩ (S11.6, mockup UI02): một work surface liền mạch
 * heading → stats → filter toolbar (search-first, một hàng ở 1440, hai hàng có tổ
 * chức ở 1280) → panel nâng cao → chip "Đã lọc" → bảng → phân trang server-side.
 *
 * Toàn bộ trạng thái đang áp dụng nằm trên URL query nên chia sẻ/back-forward đều
 * dựng lại đúng kết quả. Màn này KHÔNG có "Thêm liệt sĩ" (spec §6) và không hiển thị
 * UUID/raw enum/API metadata; export chỉ render khi actor có `EXPORT_EXECUTE` đúng
 * scope, backend vẫn là nơi cưỡng chế quyền.
 */
export function MartyrListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState<MartyrDraft>(() => readDraft(searchParams));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<
    Readonly<{ message: string; requestId?: string | undefined }> | null
  >(null);
  const [cemeteryOptions, setCemeteryOptions] = useState<
    ReadonlyArray<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    let active = true;
    void searchCemeteries({ size: 100 })
      .then((res) => {
        if (!active) return;
        const options = res.rows.map((row) => ({
          value: row.cemeteryId,
          label: row.name || row.cemeteryCode,
        }));
        setCemeteryOptions(options);
      })
      .catch(() => {
        // Không làm gián đoạn màn hình nếu không tải được danh mục nghĩa trang
      });
    return () => {
      active = false;
    };
  }, []);

  // URL là nguồn duy nhất của "đang áp dụng" → đổi query string là đổi truy vấn.
  const queryKey = searchParams.toString();
  const applied = readDraft(new URLSearchParams(queryKey));
  const page = readPage(new URLSearchParams(queryKey));
  const { options: identityStatusOptions } = useCatalog("IDENTITY_STATUS");

  const actorState = useActorContext();
  const actor: CurrentUser | null =
    actorState.status === "ready"
    || actorState.status === "refreshing"
    || actorState.status === "stale"
      ? actorState.actor
      : null;
  // Quyền hiển thị action — backend vẫn deny trực tiếp; không nhánh nào dựa trên roleCode.
  const canExport = actor !== null && can(actor, "EXPORT_EXECUTE", { type: "MARTYR" });

  const { state, refetch } = useApiResource(
    (signal) => searchMartyrs(toApiFilters(applied, page), signal),
    [queryKey],
  );

  const setData = state.status === "ready" || state.status === "refreshing" || state.status === "stale"
    ? state.data
    : null;
  const rows = setData?.rows ?? [];
  const meta = setData?.meta;

  const setField = (field: keyof MartyrDraft, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const applyDraft = (next: MartyrDraft) => {
    setDraft(next);
    setSearchParams(toSearchParams(next, 1));
  };

  const goToPage = (nextPage: number) => {
    setSearchParams(toSearchParams(applied, nextPage));
  };

  const onApply = () => {
    setSearchParams(toSearchParams(draft, 1));
  };

  const onClearAll = () => {
    applyDraft(EMPTY_DRAFT);
  };

  const onRemoveChip = (chip: MartyrChip) => {
    const next: Record<string, string> = { ...draft };
    for (const field of chip.fields) next[field] = "";
    applyDraft(next as MartyrDraft);
  };

  const onExport = async () => {
    setExporting(true);
    setExportProblem(null);
    try {
      const blob = await exportMartyrs(baseFilters(applied));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "danh-sach-liet-si.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const forbidden = error instanceof ApiProblem
        && error.problem.code === "COMMON_FORBIDDEN_SCOPE";
      const requestId = error instanceof ApiProblem ? error.problem.requestId : undefined;
      setExportProblem({
        message: forbidden
          ? "Bạn không có quyền xuất danh sách ở phạm vi hiện tại."
          : "Không thể tạo tệp xuất. Vui lòng thử lại.",
        ...(requestId !== undefined ? { requestId } : {}),
      });
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<MartyrListRow> = [
    { title: "Mã liệt sĩ", dataIndex: "martyrCode", key: "martyrCode" },
    {
      title: "Liệt sĩ",
      dataIndex: "displayName",
      key: "displayName",
      render: (_value, row) => (
        <Typography.Text style={{ fontWeight: 600 }}>
          <Link to={`/martyrs/${row.martyrId}`} aria-label={`Xem liệt sĩ ${row.martyrCode}`}>
            {row.displayName}
          </Link>
        </Typography.Text>
      ),
    },
    {
      title: "Cấp bậc · Đơn vị",
      dataIndex: "rankName",
      key: "rankName",
      render: (_value, row) => {
        const parts = [row.rankName?.trim() ?? "", row.unitName?.trim() ?? ""].filter(
          (part) => part !== "",
        );
        return parts.length === 0 ? "—" : parts.join(" · ");
      },
    },
    {
      title: "Ngày hy sinh",
      dataIndex: "sacrificeDate",
      key: "sacrificeDate",
      render: (value: string | null | undefined) => formatDateVn(value),
    },
    {
      title: "An táng",
      dataIndex: "cemeteryName",
      key: "burial",
      render: (_value, row) => <BurialCell row={row} />,
    },
    {
      title: "Trạng thái",
      dataIndex: "identityStatus",
      key: "identityStatus",
      render: (value: string, row: MartyrListRow) => (
        <StatusBadge tone={identityStatusTone(value)} label={identityStatusLabel(value, (row as Record<string, unknown>).identityStatusDisplayName)} />
      ),
    },
  ];

  const chips = buildChips(applied, cemeteryOptions, identityStatusOptions);
  const appliedAdvanced = advancedCount(applied);

  return (
    <div className="s11-list-frame">
      <PageHeading
        title="Danh sách liệt sĩ"
        description="Tra cứu liệt sĩ theo mã, họ tên, danh tính, nghĩa trang an táng và khoảng ngày hy sinh — trong đúng phạm vi dữ liệu được cấp."
        action={
          canExport ? (
            <Space>
              <Button loading={exporting} onClick={() => { void onExport(); }}>
                Xuất danh sách
              </Button>
            </Space>
          ) : undefined
        }
      />

      {meta ? (
        <Typography.Text type="secondary" className="s11-list-stats" role="status">
          {`${meta.totalElements} liệt sĩ`}
        </Typography.Text>
      ) : null}

      <section className="card s11-filters" role="search" aria-label="Bộ lọc danh sách liệt sĩ">
        <div className="s11-filter-toolbar">
          <div className="s11-filter-toolbar-search">
            <Input
              id="s11-martyr-q"
              type="search"
              placeholder="Họ tên, mã liệt sĩ hoặc quê quán..."
              aria-label="Tìm liệt sĩ"
              prefix={<SearchOutlined />}
              value={draft.q}
              onChange={(event) => setField("q", event.target.value)}
              onPressEnter={onApply}
            />
          </div>
          <div className="s11-filter-toolbar-slot">
            <Select
              id="s11-martyr-identity"
              placeholder="Tất cả danh tính"
              aria-label="Danh tính"
              popupMatchSelectWidth={false}
              allowClear
              value={draft.identityStatus === "" ? undefined : draft.identityStatus}
              onChange={(value?: string) => setField("identityStatus", value ?? "")}
              options={[...identityStatusOptions]}
            />
          </div>
          <div className="s11-filter-toolbar-slot">
            <Select
              id="s11-martyr-cemetery"
              placeholder="Tất cả nghĩa trang"
              aria-label="Nghĩa trang"
              popupMatchSelectWidth={false}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              value={draft.cemeteryId === "" ? undefined : draft.cemeteryId}
              onChange={(value?: string) => setField("cemeteryId", value ?? "")}
              options={[...cemeteryOptions]}
            />
          </div>
          <Space className="s11-filter-toolbar-actions">
            <Badge count={appliedAdvanced} size="small" offset={[-4, 4]}>
              <Tooltip title="Bộ lọc nâng cao">
                <Button
                  icon={<SlidersOutlined />}
                  aria-label="Bộ lọc nâng cao"
                  aria-expanded={advancedOpen}
                  onClick={() => setAdvancedOpen((open) => !open)}
                >
                  Nâng cao
                </Button>
              </Tooltip>
            </Badge>
            <Button icon={<ReloadOutlined />} aria-label="Làm mới" onClick={refetch} />
            <Button type="primary" onClick={onApply}>
              Tìm liệt sĩ
            </Button>
          </Space>
        </div>

        {chips.length > 0 ? (
          <div className="s11-filter-chips" aria-label="Bộ lọc đang áp dụng">
            <Typography.Text type="secondary">Đã lọc:</Typography.Text>
            {chips.map((chip) => (
              <Tag
                key={chip.label}
                closable
                onClose={(event) => {
                  event.preventDefault();
                  onRemoveChip(chip);
                }}
              >
                {chip.label}
              </Tag>
            ))}
            <Button type="link" size="small" onClick={onClearAll}>
              Xóa hết
            </Button>
          </div>
        ) : null}

        {advancedOpen ? (
          <div className="s11-filter-advanced">
            <div className="field">
              <label htmlFor="s11-martyr-from">Hy sinh từ</label>
              <DatePicker
                id="s11-martyr-from"
                aria-label="Từ ngày hy sinh"
                format={DATE_PICKER_MASK_FORMAT}
                placeholder="DD-MM-YYYY"
                style={{ width: "100%" }}
                value={draft.sacrificeFromDate !== "" ? dayjs(draft.sacrificeFromDate) : null}
                onChange={(date) =>
                  setField("sacrificeFromDate", date !== null ? date.format("YYYY-MM-DD") : "")
                }
              />
            </div>
            <div className="field">
              <label htmlFor="s11-martyr-to">Hy sinh đến</label>
              <DatePicker
                id="s11-martyr-to"
                aria-label="Đến ngày hy sinh"
                format={DATE_PICKER_MASK_FORMAT}
                placeholder="DD-MM-YYYY"
                style={{ width: "100%" }}
                value={draft.sacrificeToDate !== "" ? dayjs(draft.sacrificeToDate) : null}
                onChange={(date) =>
                  setField("sacrificeToDate", date !== null ? date.format("YYYY-MM-DD") : "")
                }
              />
            </div>
            <div className="field">
              <label htmlFor="s11-martyr-native">Quê quán</label>
              <Input
                id="s11-martyr-native"
                aria-label="Quê quán"
                value={draft.nativePlace}
                onChange={(event) => setField("nativePlace", event.target.value)}
              />
            </div>
            <div className="s11-filter-advanced-actions">
              <Button onClick={() => setAdvancedOpen(false)}>Thu gọn</Button>
              <Button
                type="primary"
                onClick={() => {
                  onApply();
                  setAdvancedOpen(false);
                }}
              >
                Áp dụng
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      {exportProblem ? (
        <Alert
          type={exportProblem.message.startsWith("Bạn không có quyền") ? "warning" : "error"}
          showIcon
          role="alert"
          message={exportProblem.message}
          description={
            exportProblem.requestId ? `Mã hỗ trợ: ${exportProblem.requestId}` : undefined
          }
          style={{ margin: "var(--ncc-spacing-sm, 8px) 1.25rem 0" }}
        />
      ) : null}

      {state.status === "loading" ? (
        <div role="status" aria-label="Đang tải danh sách liệt sĩ" className="s11-list-zone">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}
      {state.status === "forbidden" ? (
        <div className="s11-list-zone">
          <ForbiddenState description="Bạn chưa được cấp quyền đọc danh sách liệt sĩ trong phạm vi này." />
        </div>
      ) : null}
      {state.status === "error" ? (
        <div className="s11-list-zone">
          <ErrorState
            title="Không thể tải danh sách liệt sĩ"
            description="Kiểm tra bộ lọc hoặc thử lại sau ít phút."
            {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
            onRetry={refetch}
          />
        </div>
      ) : null}
      {state.status === "stale" ? (
        <div className="s11-list-zone">
          <StaleState onRefresh={refetch} />
        </div>
      ) : null}

      {setData ? (
        rows.length === 0 ? (
          <div className="s11-list-zone">
            <EmptyState
              title="Chưa tìm thấy liệt sĩ phù hợp"
              description="Thử bỏ bớt bộ lọc, đổi từ khóa hoặc kiểm tra lại mã nghĩa trang."
              action={<Button onClick={onClearAll}>Xóa hết bộ lọc</Button>}
            />
          </div>
        ) : (
          <div className="s11-table-card">
            <Table
              rowKey="martyrId"
              columns={columns}
              dataSource={rows}
              size="small"
              loading={state.status === "refreshing"}
              sticky
              onRow={(row) => ({
                className: "s11-row-clickable",
                onClick: (event) => {
                  if ((event.target as HTMLElement).closest("a")) return;
                  if ((window.getSelection()?.toString() ?? "") !== "") return;
                  void navigate(`/martyrs/${row.martyrId}`);
                },
              })}
              pagination={{
                current: meta?.page !== undefined ? meta.page + 1 : page,
                pageSize: meta?.size ?? PAGE_SIZE,
                total: meta?.totalElements ?? 0,
                showSizeChanger: false,
                showTotal: (total, range) => `Hiển thị ${range[0]}–${range[1]} / ${total}`,
                onChange: (nextPage) => goToPage(nextPage),
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
